import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { Button } from 'rebass';
import OutsideClickHandler from 'react-outside-click-handler';
import classNames from 'classnames';

import { PositionerV2 } from 'components/Positioner';
import Portal from 'components/Portal';
import InputSearch from 'components/InputSearch';
import Card from 'components/Card';

import styles from './Combobox.module.css';

/**
 * Combobox component. Kind of like a select, but with a search input.
 *
 * @param {{ value: string | number; label: string; disabled?: boolean; description?: string }[]} items List of items (options)
 * @param {string | number} value Currently selected value
 * @param {(value: string | number) => void} onChange Callback when the value changes
 * @param {string} [className]
 * @param {React.CSSProperties} [style]
 * @param {string} [ariaLabel] Aria label for the combobox
 * @param {string} [id] ID for the combobox
 */
export function Combobox({ items = [], value, onChange, className, style, ariaLabel, id }) {
    const anchorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(null);

    // Find the currently selected item’s label
    const label = useMemo(() => {
        const selectedItem = items.find(item => item.value === value);
        return selectedItem ? selectedItem.label : '';
    }, [items, value]);

    // Filter options based on the query
    const options = useMemo(() => items.filter(item => new RegExp(query, 'i').test(item.label)), [items, query]);

    const handleKeyDown = e => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (activeIndex === null) {
                setActiveIndex(0);
            } else {
                setActiveIndex(prev => Math.min(options.length - 1, prev + 1));
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (activeIndex === null) {
                setActiveIndex(options.length - 1);
            } else {
                setActiveIndex(prev => Math.max(0, prev - 1));
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const selectedOption = activeIndex !== null ? options[activeIndex] : options[0];

            if (selectedOption && !selectedOption.disabled) {
                onChange(selectedOption.value);
                setIsOpen(false);
                setQuery('');
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
        } else if (e.key === 'Tab') {
            setIsOpen(false);
        }
    };

    const handleOptionClick = option => {
        if (option.disabled) return;
        onChange(option.value);
        setIsOpen(false);
        setQuery('');
    };

    const search = query => {
        setActiveIndex(null);
        setQuery(query);
    };

    useEffect(() => {
        if (activeIndex !== null && options[activeIndex]) {
            const activeElement = document.getElementById(`option-${options[activeIndex].value}`);
            if (activeElement) {
                activeElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [activeIndex, options]);

    return (
        <>
            <Button
                id={id}
                width="100%"
                display="flex"
                className={classNames('flex items-center justify-between', className)}
                ref={anchorRef}
                variant="secondary-gray"
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-describedby="combobox-trigger-instructions"
                onClick={() => setIsOpen(true)}
                style={style}
                aria-label={ariaLabel}
            >
                {label || 'Select an option'} <FiChevronDown role="img" aria-label="Arrow down" style={{ margin: 0 }} />
            </Button>
            <div id="combobox-trigger-instructions" className="visually-hidden">
                Press Enter to open the combobox options.
            </div>
            {isOpen && (
                <Portal isOpen={isOpen}>
                    <PositionerV2 anchorRef={anchorRef}>
                        <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
                            <Card className={styles.card} style={{ width: anchorRef.current.offsetWidth }}>
                                <div className={styles.searchContainer}>
                                    <InputSearch
                                        ref={ref => ref && ref.focus({ preventScroll: true })}
                                        id="combobox-input"
                                        className="nice-underlined nice-dropdown-search"
                                        value={query}
                                        onChange={event => search(event.target.value)}
                                        aria-label="Search options"
                                        role="combobox"
                                        aria-owns="combobox-list"
                                        aria-controls="combobox-list"
                                        aria-activedescendant={
                                            activeIndex !== null && options[activeIndex]
                                                ? `option-${options[activeIndex].value}`
                                                : undefined
                                        }
                                        aria-autocomplete="list"
                                        aria-describedby="combobox-instructions"
                                        onKeyDown={handleKeyDown}
                                    />
                                    <div
                                        role="log"
                                        aria-live="polite"
                                        aria-atomic="false"
                                        aria-relevant="additions text"
                                        className="visually-hidden"
                                    >
                                        <div id="aria-results">
                                            {options.length === 1
                                                ? `1 result found for search term "${query}".`
                                                : `${options.length} results found for search term "${query}".`}
                                        </div>
                                    </div>
                                    <div id="combobox-instructions" className="visually-hidden">
                                        {options.length} result{options.length > 1 ? 's' : ''} found. Start typing to
                                        search. Use the up and down arrow keys to navigate. Press Enter to select, or
                                        Escape to close.
                                    </div>
                                </div>
                                <div id="combobox-list" role="listbox" className={styles.list}>
                                    {options.length > 0 &&
                                        options.map((option, index) => (
                                            <div
                                                role="option"
                                                aria-selected={option.value === value}
                                                aria-disabled={option.disabled || undefined}
                                                id={`option-${option.value}`}
                                                key={option.value}
                                                tabIndex={-1}
                                                onClick={() => handleOptionClick(option)}
                                                onMouseEnter={() => setActiveIndex(index)}
                                                className={classNames(
                                                    styles.option,
                                                    index === activeIndex && styles.activeOption,
                                                    option.disabled && styles.disabledOption
                                                )}
                                            >
                                                {option.label}
                                                {option.description && (
                                                    <span className={styles.optionDescription}>
                                                        {option.description}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    {options.length === 0 && (
                                        <div className={styles.empty}>
                                            <strong>0 results</strong>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </OutsideClickHandler>
                    </PositionerV2>
                </Portal>
            )}
        </>
    );
}
